import { CheckOutlined, TeamOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { AccordionAPI } from '@mm/components/src/AccordionItem/AccordionItem';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { ShopifyIcon } from '@mm/icons';
import { useShopifyCRUDActions } from '@mm/state/actions/ShopifyCRUDActions';
import ConnectionModel, { AccessToken } from '@mm/state/model/ConnectionModel';
import { selectShopifyConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectShopifySuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { KlaviyoErrorMessage } from '../KlaviyoIntegrations/KlaviyoErrorMessage';
import styles from './ShopifyIntegrations.module.css';
import { ShopifySettings } from './ShopifySettings';
import { useConnectShopify } from './use-connect-shopify';

const header = (
  connection: ConnectionModel | undefined,
  oauthTokens: AccessToken[],
  showSyncWarning: boolean,
  onClick: () => void,
  version: 'connections' | 'suppression',
  leadsSent?: number,
) => {
  const matchingOauthToken = oauthTokens.find(
    token =>
      token.id === connection?.accessToken?.id && token.status === 'active',
  );
  const readOnly = matchingOauthToken?.readOnly;

  return (
    <div className="integrations__header-actions">
      {showSyncWarning && (
        <KlaviyoErrorMessage
          classNames={styles.syncWarning}
          error="Not Syncing"
          iconSize="16px"
        />
      )}
      {!matchingOauthToken ? (
        <Button
          css={{
            container: classNames('integrations__header-add-button-secondary', {
              'integrations__header-add-button-secondary-static-disabled':
                !!readOnly,
            }),
          }}
          type="button"
          disabled={!!readOnly}
          onClick={e => {
            e?.stopPropagation();
            if (readOnly) {
              return;
            }
            onClick();
          }}
          color={ButtonColor.secondaryBlue}
        >
          Sign In to Shopify
        </Button>
      ) : (
        <>
          {!!leadsSent && (
            <div className="integrations__header-status">
              <TeamOutlined className="integrations__header-status-icon" />
              <div className="integrations__header-status-count">
                {leadsSent}
              </div>
              {version === 'connections' ? 'Leads sent' : 'Profiles suppressed'}
            </div>
          )}

          <Button
            css={{
              container: classNames(
                'integrations__header-add-button-secondary',
                'integrations__header-add-button-secondary-static-disabled',
              ),
            }}
            type="button"
            disabled
            leftIcon={<CheckOutlined />}
            color={ButtonColor.secondaryBlue}
          >
            Signed In
          </Button>
        </>
      )}
    </div>
  );
};

export const ShopifyIntegrations = ({
  version = 'connections',
}: {
  version: 'connections' | 'suppression';
}) => {
  const {
    getShopifyAccounts,
    connectShopify,
    getShopifyConnections,
    connectionsMessage,
  } = useConnectShopify();

  const shopifyConnections = useSelector(selectShopifyConnections);
  const suppressions = useSelector(selectShopifySuppressionList);
  const suppression = suppressions[0];

  const tokens = useSelector((state: StoreState) => state.shopify.accounts);

  const { invalidateStore } = useShopifyCRUDActions();
  const [accountsFetched, setAccountsFetched] = useState(false);

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const sectionProps = {
    landmineName: landmineNames.SHOPIFY_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };

  const upgradeOrCreateButtonName = 'Sign In to Shopify';

  const shopifyConnection = shopifyConnections?.[0];
  const token = tokens?.find(t => t.status === 'active');

  const showSyncWarning =
    version === 'connections' && token && shopifyConnection
      ? !!(connectionsMessage || token.errorInfo)
      : false;

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent =
    version === 'connections'
      ? crmsLeadsSent?.shopifyContactsSent
      : suppression?.profilesCount;

  useEffect(() => {
    if (accountsFetched) {
      getShopifyConnections();
    }
  }, [accountsFetched]);

  useEffect(() => {
    (async () => {
      await getShopifyAccounts();
      setAccountsFetched(true);
    })();
    return () => {
      invalidateStore();
    };
  }, []);

  const ref = useRef<AccordionAPI>(null);

  return (
    <BaseIntegration
      accordionRef={ref}
      titleComponent={
        <BaseIntegrationHeader
          title="Shopify"
          upgradeButtonName={upgradeOrCreateButtonName}
          subtitle={
            version === 'connections'
              ? `Automatically sync your ${companyName} contacts to Shopify`
              : ''
          }
          icon={<ShopifyIcon />}
          headerRightComponent={header(
            shopifyConnection,
            tokens,
            showSyncWarning,
            connectShopify,
            version,
            leadsSent,
          )}
          css={{ icon: 'integrations__shopify-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="shopify"
      emptyStateConfig={{
        button: upgradeOrCreateButtonName,
        onClick: connectShopify,
        icon: (
          <div
            className={classNames(
              'integrations__header-icon',
              'integrations__shopify-header-icon',
            )}
          >
            {' '}
            <ShopifyIcon />
          </div>
        ),
        landmineName: landmineNames.SHOPIFY_UPGRADE,
        featureFlag: 'nativeIntegrations',
      }}
    >
      <ShopifySettings
        connectToken={connectShopify}
        connectionsMessage={connectionsMessage}
        version={version}
      />
    </BaseIntegration>
  );
};
